import { gsap } from 'gsap';
import React, { createRef, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDidMount } from '../helpers/useDidMount';
var Size;
(function (Size) {
    Size["Default"] = "default";
    Size["Small"] = "small";
    Size["Tiny"] = "tiny";
})(Size || (Size = {}));
var Variant;
(function (Variant) {
    Variant["Primary"] = "primary";
    Variant["Secondary"] = "secondary";
    Variant["Line"] = "line";
})(Variant || (Variant = {}));
const SegmentedControlWrapper = styled.div `
  display: inline-flex;
  vertical-align: top;
  box-sizing: border-box;
  gap: 24px;
  max-height: max-content;

  cursor: ${({ isDisabled }) => isDisabled && 'not-allowed'};
  opacity: ${({ isDisabled }) => isDisabled && 0.5};

  width: ${({ fullWidth, fullWidthEqual }) => (fullWidth || fullWidthEqual ? '100%' : 'min-content')};

  ${({ variant }) => variant === Variant.Primary &&
    css `
      gap: ${({ theme }) => theme.style.segmentedControlGap};
      padding: ${({ theme }) => theme.style.segmentedControlPadding};
      border-radius: ${({ theme }) => theme.style.segmentedControlBorderRadius};
      background-color: ${({ theme }) => theme.style.segmentedControlBackgroundColor};
      box-shadow:
        ${({ theme }) => theme.style.segmentedControlBorderProperties}
          ${({ theme }) => theme.style.segmentedControlBorderWidth}
          ${({ theme }) => theme.style.segmentedControlBorderColor},
        ${({ theme }) => theme.style.segmentedControlShadow};
    `}

  ${({ variant }) => variant === Variant.Secondary &&
    css `
      position: relative;
      margin: 0;
      gap: 8px;
      border: 0;
      box-shadow: none;
      display: flex;

      &:before {
        content: '';
        height: 2px;
        background: linear-gradient(
          90deg,
          ${({ theme }) => theme.primary.blendToBackground(400)} 0%,
          ${({ theme }) => theme.primary.background} 50%,
          ${({ theme }) => theme.primary.blendToBackground(400)} 100%
        );
        position: absolute;
        left: var(--segmentedcontrol-active-left, 0);
        width: var(--segmentedcontrol-active-width, 0);
        top: calc(100% - 1px);
        box-shadow: 0 0 16px ${({ theme }) => theme.primary.blendToBackground(1000, 200)};
        transition: left 0.1s ease-out;
      }

      &::before {
        transition:
          left 0.05s ease-out,
          width 0.02s ease-out;
      }

      &::before {
        height: auto;
        top: 0;
        bottom: 0;
        z-index: 0;
        --option-active-background: ${({ theme }) => theme.primary.blendToBackground(200)};
        border-radius: 8px;
        box-shadow: none;

        ${({ theme }) => theme.id !== 'default' &&
        css `
            --option-active-background: ${theme.primary.blendToBackground(1000, 200)};
          `}
      }
    `}

  ${({ variant }) => variant === Variant.Line &&
    css `
      position: relative;
      box-shadow: inset 0 -1px 0 ${({ theme }) => theme.secondary.blendToBackground(200)};

      &:before {
        content: '';
        height: 1px;
        background: linear-gradient(
          90deg,
          ${({ theme }) => theme.primary.blendToBackground(300)} 0%,
          ${({ theme }) => theme.primary.background} 50%,
          ${({ theme }) => theme.primary.blendToBackground(300)} 100%
        );
        position: absolute;
        left: var(--segmentedcontrol-active-left, 0);
        width: var(--segmentedcontrol-active-width, 0);
        top: calc(100% - 1px);
        box-shadow:
          0 0 1px ${({ theme }) => theme.primary.blendToBackground(800)}25,
          0 1px 4px ${({ theme }) => theme.primary.blendToBackground(800)};
        transition: left 0.035s cubic-bezier(0.4, 0, 0.2, 1);
      }
    `}

  ${({ size }) => size === Size.Small &&
    css `
      gap: 16px;

      ${({ variant }) => variant !== Variant.Line &&
        css `
          gap: ${({ theme }) => theme.style.segmentedControlSmallGap};
          padding: ${({ theme }) => theme.style.segmentedControlSmallPadding};
          border-radius: ${({ theme }) => theme.style.segmentedControlSmallBorderRadius};
        `}
    `}

  ${({ size }) => size === Size.Tiny &&
    css `
      gap: 12px;

      ${({ variant }) => variant !== Variant.Line &&
        css `
          gap: ${({ theme }) => theme.style.segmentedControlTinyGap};
          padding: ${({ theme }) => theme.style.segmentedControlTinyPadding};
          border-radius: ${({ theme }) => theme.style.segmentedControlTinyBorderRadius};
        `}
    `}
`;
export const SegmentedControlOption = styled.button `
  appearance: none;
  outline: none;
  border: none;
  background: none;
  display: flex;
  white-space: nowrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.style.formElementFontSizeM};
  font-weight: 500;
  padding: 7px 16px;
  line-height: 20px;
  color: var(--option-color, ${({ theme }) => theme.style.segmentedControlOptionColor});
  transition: color 0.1s;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  position: relative;

  flex-grow: ${({ fullWidth, fullWidthEqual }) => (fullWidth || fullWidthEqual) && '1'};
  justify-content: ${({ fullWidth, fullWidthEqual }) => (fullWidth || fullWidthEqual) && 'center'};

  flex-basis: ${({ fullWidthEqual }) => fullWidthEqual && '0'};

  ${({ variant }) => variant === Variant.Primary &&
    css `
      border-radius: ${({ theme }) => theme.style.segmentedControlOptionBorderRadius};
      background-color: var(--option-background, transparent);
      transition:
        color 0.2s,
        background-color 0.2s,
        box-shadow var(--box-shadow-duration, 0.2s),
        transform 0.2s;
      transform: scale(var(--option-scale, 1)) translateZ(0);

      &:hover {
        --option-background: ${({ theme }) => theme.style.segmentedControlOptionHoveredBackgroundColor};
        --option-color: ${({ theme }) => theme.style.segmentedControlOptionHoveredColor};
      }
      &:active {
        --option-scale: 0.975;
      }

      ${({ $isActive }) => $isActive &&
        css `
          --option-active-color: ${({ theme }) => theme.style.segmentedControlOptionActiveColor};
          --option-active-background: ${({ theme }) => theme.style.segmentedControlOptionActiveBackgroundColor};
          --box-shadow-duration: 0.35s;

          color: var(--option-active-color, ${({ theme }) => theme.style.segmentedControlOptionColor});
          background-color: var(--option-active-background, transparent);
          box-shadow: ${({ theme }) => theme.style.segmentedControlOptionActiveShadow};

          &:hover {
            --option-active-background: ${({ theme }) => theme.style.segmentedControlOptionActiveBackgroundColor};
            --option-active-color: ${({ theme }) => theme.style.segmentedControlOptionActiveColor};
          }
        `}

      ${({ $isActive }) => !$isActive &&
        css `
          &:hover {
            --option-background: ${({ theme }) => theme.style.segmentedControlOptionHoveredBackgroundColor};
          }
        `}
    `}

  ${({ variant }) => variant === Variant.Secondary &&
    css `
      z-index: 1;
      position: relative;
      box-shadow: none;
      --option-color: ${({ theme }) => theme.secondary.background};
      --option-active-color: ${({ theme }) => theme.primary.background};

      ${({ theme }) => theme.id !== 'default' &&
        css `
          --option-color: ${theme.secondary.background};
        `}

      ${({ $isActive }) => $isActive &&
        css `
          --option-active-color: ${({ theme }) => theme.primary.background};

          color: var(--option-active-color, ${({ theme }) => theme.style.segmentedControlOptionColor});
          background-color: var(--option-active-background, transparent);

          ${({ theme }) => theme.id !== 'default' &&
            css `
              --option-color: ${theme.secondary.background};
              --option-active-color: ${theme.primary.blendToBackground(1300)};
            `}
        `}

      ${({ $isActive }) => !$isActive &&
        css `
          &:hover {
            --option-color: ${({ theme }) => theme.secondary.background};
          }
        `}
    `}

  ${({ variant }) => variant === Variant.Line &&
    css `
      padding: 12px 0;
      --option-color: ${({ theme }) => theme.secondary.background};

      ${({ $isActive }) => $isActive &&
        css `
          --option-active-color: ${({ theme }) => theme.primary.background};

          color: var(--option-active-color, ${({ theme }) => theme.style.segmentedControlOptionColor});
          background-color: var(--option-active-background, transparent);
        `}

      ${({ $isActive }) => !$isActive &&
        css `
          &:hover {
            --option-color: ${({ theme }) => theme.secondary.background};
          }
        `}
    `}

  svg {
    --icon-size: 20px;
    --icon-color: currentColor;
    margin: 0 -4px;

    ${({ withIcon }) => withIcon &&
    css `
        margin: 0 0 0 -8px;
        opacity: 0.8;
      `}
  }

  ${({ size }) => size === Size.Default &&
    css `
      ${({ variant }) => variant === Variant.Secondary &&
        css `
          padding: 7px 12px;
        `}
    `}

  ${({ size }) => size === Size.Small &&
    css `
      padding: ${({ theme }) => theme.style.segmentedControlSmallOptionPadding};

      ${({ variant }) => variant === Variant.Line &&
        css `
          padding: 0 0 8px 0;
        `}
    `}

  ${({ size }) => size === Size.Tiny &&
    css `
      padding: ${({ theme }) => theme.style.segmentedControlTinyOptionPadding};
      font-size: ${({ theme }) => theme.style.segmentedControlTinyOptionFontSize};
      font-weight: ${({ theme }) => theme.style.segmentedControlTinyOptionFontWeight};
      border-radius: ${({ theme }) => theme.style.segmentedControlTinyOptionBorderRadius};

      ${({ variant }) => variant === Variant.Line &&
        css `
          padding: 0 0 6px 0;
        `}
    `}
`;
const DropdownContainer = styled.div `
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.secondary.blendToBackground(25)};
  box-shadow:
    0 2px 8px ${({ theme }) => theme.secondary.blendToBackground(1000, 100)},
    0 1px 0 rgba(0, 0, 0, 0.05);
  z-index: 1000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-8px)')};
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease,
    transform 0.2s ease;
`;
const DropdownOption = styled.button `
  width: 100%;
  padding: 12px;
  border: none;
  background: ${({ $isActive, theme }) => ($isActive ? theme.primary.blendToBackground(200) : 'transparent')};
  color: ${({ $isActive, theme }) => ($isActive ? theme.primary.background : theme.secondary.background)};
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  font-size: ${({ theme }) => theme.style.formElementFontSizeM};
  font-weight: 500;

  &:hover {
    background: ${({ theme }) => theme.secondary.blendToBackground(100)};
    color: ${({ theme }) => theme.secondary.background};
  }
`;
const ResponsiveWrapper = styled.div `
  position: relative;
  display: ${({ $isResponsive }) => ($isResponsive ? 'block' : 'contents')};
`;
const ActiveTabButton = styled.button `
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.secondary.background};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  font-size: ${({ theme }) => theme.style.formElementFontSizeM};
  font-weight: 500;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
  transition: color 0.15s ease;

  svg {
    transition: transform 0.2s ease;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;
export const SegmentedControl = (props) => {
    const wrapperRef = createRef();
    const didMount = useDidMount();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isResponsive, setIsResponsive] = useState(false);
    const styleProps = {
        size: props.size ?? Size.Default,
        variant: props.variant ?? Variant.Primary,
        withIcon: props.withIcon ?? false,
        isDisabled: props.isDisabled ?? false,
        fullWidth: props.fullWidth ?? false,
        fullWidthEqual: props.fullWidthEqual ?? false,
    };
    const isActive = useCallback((value, active, removeWildcard) => {
        if (typeof active !== 'undefined' || props.activeUndefined) {
            if (props.activeWildcard && typeof active === 'string' && typeof value === 'string' && !removeWildcard) {
                return active.startsWith(value) || value === active;
            }
            return Array.isArray(value) && active ? value.includes(active.toString()) : value === active;
        }
        return false;
    }, [props.activeWildcard, props.activeUndefined]);
    const handleClick = useCallback((option) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onOptionChange) {
            props.onOptionChange(Array.isArray(option.value) ? option.value[0] : option.value);
        }
        if (option.onClick) {
            option.onClick(Array.isArray(option.value) ? option.value[0] : option.value);
        }
        if (isResponsive && props.variant === Variant.Line) {
            setIsDropdownOpen(false);
        }
    }, [props.isDisabled, props.onOptionChange, isResponsive, props.variant]);
    const handleDropdownToggle = useCallback(() => {
        if (props.isDisabled) {
            return;
        }
        setIsDropdownOpen(!isDropdownOpen);
    }, [isDropdownOpen, props.isDisabled]);
    const checkResponsive = useCallback(() => {
        if (props.variant !== Variant.Line || !props.isResponsive) {
            setIsResponsive(false);
            return;
        }
        // Use tablet breakpoint (768px) to determine responsive mode
        const isTabletOrSmaller = window.innerWidth <= 768;
        setIsResponsive(isTabletOrSmaller);
    }, [props.variant, props.isResponsive]);
    const updateActiveLinePosition = useCallback((duration = 0.1) => {
        const wrapper = wrapperRef.current;
        if (!wrapper || props.variant === Variant.Primary) {
            return;
        }
        const current = props.options.findIndex((e) => isActive(e.value, props.active, e.removeWildcard));
        const activeElement = wrapper.children.item(current);
        if (activeElement) {
            const left = activeElement.offsetLeft;
            const width = activeElement.offsetWidth;
            gsap.to(wrapper, {
                '--segmentedcontrol-active-left': `${left}px`,
                '--segmentedcontrol-active-width': `${width}px`,
                duration,
                ease: 'ease-out',
            });
        }
    }, [props.active, props.options, props.variant, wrapperRef, isActive]);
    useEffect(() => {
        if (props.variant === Variant.Secondary || props.variant === Variant.Line) {
            window.addEventListener('resize', () => updateActiveLinePosition(0));
            document.addEventListener('DOMContentLoaded', () => updateActiveLinePosition(0));
            document.fonts.ready.then(() => updateActiveLinePosition(0));
            return () => {
                window.removeEventListener('resize', () => updateActiveLinePosition(0));
                document.removeEventListener('DOMContentLoaded', () => updateActiveLinePosition(0));
            };
        }
        return;
    }, [props.variant]);
    useEffect(() => {
        if (didMount && (props.variant === Variant.Secondary || props.variant === Variant.Line)) {
            updateActiveLinePosition(0.3);
        }
    }, [props.active, didMount, updateActiveLinePosition, props.variant]);
    useEffect(() => {
        if (didMount) {
            checkResponsive();
        }
        window.addEventListener('resize', checkResponsive);
        return () => window.removeEventListener('resize', checkResponsive);
    }, [checkResponsive, didMount]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
        return undefined;
    }, [isDropdownOpen]);
    const activeOption = props.options.find((option) => isActive(option.value, props.active, option.removeWildcard));
    if (isResponsive && props.variant === Variant.Line) {
        return (React.createElement(ResponsiveWrapper, { "$isResponsive": isResponsive },
            React.createElement(ActiveTabButton, { "$isDisabled": props.isDisabled ?? false, "$isOpen": isDropdownOpen, onClick: handleDropdownToggle, type: "button" },
                activeOption?.label || props.options[0]?.label,
                React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "currentColor" },
                    React.createElement("path", { d: "M4 6l4 4 4-4", stroke: "currentColor", strokeWidth: "1.5", fill: "none", strokeLinecap: "round", strokeLinejoin: "round" }))),
            React.createElement(DropdownContainer, { "$isOpen": isDropdownOpen }, props.options.map((option, index) => (React.createElement(DropdownOption, { key: index, "$isActive": isActive(option.value, props.active, option.removeWildcard), onClick: () => handleClick(option), type: "button" }, option.label))))));
    }
    return (React.createElement(SegmentedControlWrapper, { className: props.className, ref: wrapperRef, ...styleProps }, props.options.map((option, index) => (React.createElement(SegmentedControlOption, { onClick: () => {
            handleClick(option);
        }, "$isActive": isActive(option.value, props.active, option.removeWildcard), key: index, type: "button", ...styleProps }, option.label)))));
};
export { Size as SegmentedControlSize, Variant as SegmentedControlVariant };
//# sourceMappingURL=SegmentedControl.js.map